import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Descriptions, Drawer, Select, Space, Table, Tag, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import dayjs from 'dayjs';
import {
  entitlementLedgerApi,
  type LedgerEntryListItem,
  type LedgerEntryType,
  type StudentAccountSummary,
} from '../../api/entitlementLedger.api';
import { entitlementApi, type PeriodPassView } from '../../api/entitlement.api';
import { studentsApi } from '../../api/students.api';
import { deductionRecordsApi, type DeductionRecordItem } from '../../api/deduction.api';
import { EllipsisText } from '../../components/EllipsisCell';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { useHasPermission } from '../../auth/HasPermission';
import { useGlobalCampusScope } from '../../auth/GlobalCampusContext';
import {
  LedgerDetailDrawer,
  ledgerBusinessRefLabel,
  ledgerStatusTag,
  ledgerTypeLabel,
  lessonKindLabel,
} from './LedgerDetailDrawer';
import { BusinessRefFallbackDrawer } from './BusinessRefFallbackDrawer';
import { PeriodAdjustModal } from './PeriodAdjustModal';
import { PeriodPassSummaryDrawer } from './PeriodPassSummaryDrawer';
import { periodLedgerSummary } from '../../utils/period-ledger';
import {
  ManualTrialGrantDetailSection,
  useManualTrialGrantDetail,
} from './ManualTrialGrantDetailSection';
import { EnrollmentDetailDrawer } from '../students/EnrollmentDetailDrawer';
import { OrderDetailDrawer } from '../billing/OrderDetailDrawer';
import { ApiError } from '../../api/transport';

const { Title, Text } = Typography;

const typeOptions = Object.entries(ledgerTypeLabel).map(([value, label]) => ({
  value,
  label,
}));

function formatDateTime(value: string): string {
  return dayjs(value).format('YYYY-MM-DD HH:mm');
}

/**
 * 业务编号下钻（SC-15/SC-16）：报名→报名抽屉（3-2 复用）、订单→订单抽屉（4-1 复用）、
 * 消课/冲正→批次详情抽屉（6-1 复用，按 businessRefId 为课次上下文不可直查时回退说明抽屉）、
 * 手动赠送→赠课记录明细抽屉（manual-trial-grant，SC-35 下钻）、
 * 期限卡→期限卡只读摘要抽屉（O-14 period-pass-admin-ui:SC-11）。
 */
type RefTarget =
  | { kind: 'enrollment'; id: string }
  | { kind: 'order'; id: string }
  | { kind: 'deduction'; id: string }
  | { kind: 'reversal'; id: string }
  | { kind: 'manualGrant'; id: string }
  | { kind: 'periodPass'; id: string }
  | { kind: 'fallback'; refType: LedgerEntryListItem['businessRefType']; refId: string }
  | null;

/** ADM-24 学生课时账本：三联动筛选 + 摘要卡 + 期限卡卡片区 + 全机构流水倒序（无操作列，只读）。 */
/** 冲正引用的原流水详情加载（更正链；404 经 onNotFound 回退说明抽屉）。 */
function useEntryDetailWhen(
  entryId: string | null,
  onNotFound?: (entryId: string) => void,
) {
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof entitlementLedgerApi.getEntry>> | null>(null);
  // onNotFound 经 ref 转发，调用方可传内联函数而不重触发加载
  const notFoundRef = useRef(onNotFound);
  notFoundRef.current = onNotFound;
  useEffect(() => {
    if (!entryId) {
      setDetail(null);
      return;
    }
    void (async () => {
      try {
        setDetail(await entitlementLedgerApi.getEntry(entryId));
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          notFoundRef.current?.(entryId);
          return;
        }
        setDetail(null);
      }
    })();
  }, [entryId]);
  return detail;
}

/** 消课来源业务编号 → 消课明细只读视图（admin-reversal-and-ledger-ui:SC-15；404 回退说明）。 */
function DeductionRefDrawer({
  deductionId,
  onClose,
  onNotFound,
}: {
  deductionId: string | null;
  onClose: () => void;
  onNotFound: (deductionId: string) => void;
}) {
  const [record, setRecord] = useState<DeductionRecordItem | null>(null);
  // onNotFound 经 ref 转发，调用方可传内联函数而不重触发加载
  const notFoundRef = useRef(onNotFound);
  notFoundRef.current = onNotFound;
  useEffect(() => {
    if (!deductionId) {
      setRecord(null);
      return;
    }
    void (async () => {
      try {
        setRecord(await deductionRecordsApi.getDeduction(deductionId));
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          notFoundRef.current?.(deductionId);
          return;
        }
        setRecord(null);
      }
    })();
  }, [deductionId]);
  return (
    <Drawer title="消课明细" open={Boolean(record)} onClose={onClose} width={520} destroyOnHidden>
      {record && (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="明细编号">{record.id}</Descriptions.Item>
          <Descriptions.Item label="批次编号">{record.batchId}</Descriptions.Item>
          <Descriptions.Item label="课次日期">{record.sessionDate}</Descriptions.Item>
          <Descriptions.Item label="班级">{record.className}</Descriptions.Item>
          <Descriptions.Item label="学生">
            {record.studentName}（{record.studentNo}）
          </Descriptions.Item>
          <Descriptions.Item label="扣减数量">{record.quantity}</Descriptions.Item>
          <Descriptions.Item label="关联流水号">
            {record.ledgerEntryId ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="操作人">{record.operatorName ?? '-'}</Descriptions.Item>
        </Descriptions>
      )}
    </Drawer>
  );
}

/** 手动赠送来源业务编号 → 赠课记录明细只读抽屉（entitlement-core:SC-35；404 回退说明）。 */
function ManualGrantRefDrawer({
  recordId,
  onClose,
  onNotFound,
}: {
  recordId: string | null;
  onClose: () => void;
  onNotFound: (recordId: string) => void;
}) {
  const { detail, loading, failed } = useManualTrialGrantDetail(recordId, onNotFound);
  return (
    <Drawer title="赠课记录" open={Boolean(recordId)} onClose={onClose} width={520} destroyOnHidden>
      <ManualTrialGrantDetailSection detail={detail} loading={loading} failed={failed} />
    </Drawer>
  );
}

/**
 * 期限卡卡片区（O-14，period-pass-admin-ui:SC-09）：账户摘要区下方按购买先后渲染学生期限卡，
 * 状态徽标（待激活/生效中/已到期派生态）+ 区间/剩余天数/期限天数/来源订单 + 自动接续说明。
 * 「调整开始日期」入口挂 ADM-24:PERIOD_ADJUST 权限（无权限隐藏而非禁用）且仅未到期卡显示。
 */
function PeriodPassSection({
  passes,
  canAdjust,
  onAdjust,
}: {
  passes: PeriodPassView[];
  canAdjust: boolean;
  onAdjust: (pass: PeriodPassView) => void;
}) {
  if (passes.length === 0) return null;
  return (
    <Card size="small" style={{ marginTop: 12 }} data-testid="period-pass-section">
      <Text strong>
        期限卡（按购买先后排队，前卡到期后的下一次正式消课自动接续）
      </Text>
      {passes.map((p) => {
        const statusLabel = periodPassStatusLabel(p.displayStatus);
        return (
          <div
            key={p.periodPassId}
            data-testid="period-pass-card"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginTop: 8,
              flexWrap: 'wrap',
            }}
          >
            <Tag color={periodPassTagColor(p.displayStatus)}>期限卡 · {statusLabel}</Tag>
            {p.startDate && p.endDate ? (
              p.displayStatus === 'EXPIRED' ? (
                <Text type="secondary">
                  {p.startDate} ~ {p.endDate}（期限 {p.periodDays} 天，含当天）
                </Text>
              ) : (
                <Text>
                  {p.startDate} ~ {p.endDate}（期限 {p.periodDays} 天，含当天）
                </Text>
              )
            ) : (
              <Text>期限 {p.periodDays} 天 · 首次上课自动起算（不设起始日）</Text>
            )}
            {p.displayStatus === 'ACTIVE' && (
              <Text type="success" data-testid={`period-pass-remaining-${p.periodPassId}`}>
                剩余 {p.remainingDays} 天
              </Text>
            )}
            <Text type="secondary">来源订单 {p.sourceOrderNo ?? p.sourceOrderId}</Text>
            {p.displayStatus !== 'EXPIRED' && canAdjust && (
              <Button
                size="small"
                data-testid={`period-adjust-${p.periodPassId}`}
                onClick={() => onAdjust(p)}
              >
                调整开始日期
              </Button>
            )}
          </div>
        );
      })}
    </Card>
  );
}

/** 期限卡展示状态中文标签。 */
function periodPassStatusLabel(displayStatus: string): string {
  if (displayStatus === 'ACTIVE') return '生效中';
  if (displayStatus === 'EXPIRED') return '已到期';
  return '待激活';
}

/** 期限卡卡片状态徽标颜色（生效中绿 / 待激活金 / 已到期灰）。 */
function periodPassTagColor(displayStatus: string): string {
  if (displayStatus === 'ACTIVE') return 'green';
  if (displayStatus === 'EXPIRED') return 'default';
  return 'gold';
}

export function LedgerPage() {
  const [rows, setRows] = useState<LedgerEntryListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // 三联动筛选
  const [studentId, setStudentId] = useState<string | undefined>();
  const [accountId, setAccountId] = useState<string | undefined>();
  const [entryType, setEntryType] = useState<string | undefined>();
  // 学生选项：远程联想（/students/search 脱敏接口，300ms 防抖），不一次性拉全量列表
  const [studentOptions, setStudentOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [selectedStudent, setSelectedStudent] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [studentKeyword, setStudentKeyword] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [accounts, setAccounts] = useState<StudentAccountSummary[]>([]);
  // 摘要卡（选定账户）
  const [summary, setSummary] = useState<StudentAccountSummary | null>(null);
  // 期限卡卡片区（学生选定后加载，O-14）
  const [periodPasses, setPeriodPasses] = useState<PeriodPassView[]>([]);
  const [adjustingPass, setAdjustingPass] = useState<PeriodPassView | null>(null);
  // 调整入口挂 ADM-24:PERIOD_ADJUST 动作（无权限隐藏按钮而非禁用态，SC-12）
  const canAdjust = useHasPermission('ADM-24', 'PERIOD_ADJUST');
  // 抽屉
  const [entryDetailId, setEntryDetailId] = useState<string | null>(null);
  const [refTarget, setRefTarget] = useState<RefTarget>(null);
  // 全局校区范围：'ALL' 不携带 campusId（锁定模式）；指定校区随请求收窄（O-27）
  const { scope } = useGlobalCampusScope();

  const pageSize = 20;

  const loadEntries = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      try {
        const res = await entitlementLedgerApi.listEntries({
          campusId: scope === 'ALL' ? undefined : scope,
          studentId,
          accountId,
          type: entryType as LedgerEntryType | undefined,
          page: targetPage,
          pageSize,
        });
        setRows(res.items);
        setTotal(res.total);
        setPage(targetPage);
      } catch {
        message.error('加载账本流水失败');
      } finally {
        setLoading(false);
      }
    },
    [studentId, accountId, entryType, scope],
  );

  useEffect(() => {
    void loadEntries(1);
  }, [loadEntries]);

  // 学生联想（输入触发，走脱敏联想接口，按姓名/学号；沿用班级抽屉同款 300ms 防抖）
  const handleStudentSearch = (keyword: string) => {
    setStudentKeyword(keyword);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!keyword.trim()) {
      setStudentOptions([]);
      return;
    }
    searchTimer.current = setTimeout(() => {
      void (async () => {
        try {
          const items = await studentsApi.searchStudents(keyword.trim());
          setStudentOptions(
            items.map((s) => ({ value: s.id, label: `${s.name}（${s.studentNo}）` })),
          );
        } catch {
          setStudentOptions([]);
        }
      })();
    }, 300);
  };

  /** 已选项保持在选项首位，防止远程选项刷新后选中项显示裸 UUID。 */
  const studentSelectOptions = selectedStudent
    ? [
        selectedStudent,
        ...studentOptions.filter((o) => o.value !== selectedStudent.value),
      ]
    : studentOptions;

  const handleStudentChange = (value: string | undefined) => {
    setStudentId(value);
    setSelectedStudent(
      value
        ? studentSelectOptions.find((o) => o.value === value) ?? null
        : null,
    );
  };

  // 选定学生后加载其账户选项与期限卡卡片区；切换学生时重置账户与摘要卡（SC-11）
  useEffect(() => {
    setAccountId(undefined);
    setSummary(null);
    if (!studentId) {
      setAccounts([]);
      return;
    }
    void (async () => {
      try {
        setAccounts(await entitlementLedgerApi.getStudentAccounts(studentId));
      } catch {
        setAccounts([]);
      }
    })();
  }, [studentId]);

  // 期限卡卡片区：学生选定后加载（按购买先后排序由服务端保证，SC-09）
  const reloadPeriodPasses = useCallback(async () => {
    if (!studentId) {
      setPeriodPasses([]);
      return;
    }
    try {
      setPeriodPasses(await entitlementApi.listStudentPeriodPasses(studentId));
    } catch {
      setPeriodPasses([]);
    }
  }, [studentId]);

  useEffect(() => {
    void reloadPeriodPasses();
  }, [reloadPeriodPasses]);

  // 选定账户后展示摘要卡
  useEffect(() => {
    setSummary(accounts.find((account) => account.accountId === accountId) ?? null);
  }, [accountId, accounts]);

  const { columns, components } = useResizableColumns<LedgerEntryListItem>([
    {
      title: '流水号',
      dataIndex: 'id',
      render: (v) => (
        <a onClick={() => setEntryDetailId(v)}>
          <EllipsisText value={v} />
        </a>
      ),
    },
    { title: '更新时间', dataIndex: 'createdAt', render: (v) => formatDateTime(v) },
    {
      title: '学生',
      dataIndex: 'studentName',
      render: (_, record) => `${record.studentName}（${record.studentNo}）`,
    },
    {
      title: '账户',
      dataIndex: 'skuName',
      render: (_, record) =>
        `${record.skuName} · ${lessonKindLabel[record.lessonKind] ?? record.lessonKind}`,
    },
    {
      title: '类型',
      dataIndex: 'type',
      render: (v) => <Tag>{ledgerTypeLabel[v] ?? v}</Tag>,
    },
    {
      title: '赠送课时（前→后）',
      width: 130,
      render: (_, record) =>
        // PERIOD 事件行池变动恒 0：三组数量列以「—」呈现（period-pass-admin-ui:SC-10）
        record.lessonKind === 'PERIOD' ? '—' : `${record.giftedBefore} → ${record.giftedAfter}`,
    },
    {
      title: '购买课时（前→后）',
      width: 130,
      render: (_, record) =>
        record.lessonKind === 'PERIOD' ? '—' : `${record.formalBefore} → ${record.formalAfter}`,
    },
    {
      title: '可用课时（前→后）',
      width: 220,
      render: (_, record) =>
        // PERIOD 行可用课时列代之以期限卡语义摘要（开卡/起算区间/调整/使用，共享派生函数）
        record.lessonKind === 'PERIOD'
          ? periodLedgerSummary(record)
          : `${record.formalBefore + record.giftedBefore} → ${record.formalAfter + record.giftedAfter}`,
    },
    {
      title: '业务来源',
      dataIndex: 'businessRefType',
      render: (v) => ledgerBusinessRefLabel[v as LedgerEntryListItem['businessRefType']] ?? v,
    },
    {
      title: '业务编号',
      dataIndex: 'businessRefId',
      render: (v, record) => (
        <a
          onClick={() => {
            if (record.businessRefType === 'ENROLLMENT') {
              setRefTarget({ kind: 'enrollment', id: v });
            } else if (record.businessRefType === 'ORDER') {
              setRefTarget({ kind: 'order', id: v });
            } else if (record.businessRefType === 'DEDUCTION') {
              // 消课来源：businessRefId 为消课明细 ID → 打开消课明细视图（SC-15）
              setRefTarget({ kind: 'deduction', id: v });
            } else if (record.businessRefType === 'REVERSAL') {
              // 冲正来源：businessRefId 为被冲正原流水 ID → 打开原流水详情形成更正链（SC-05）
              setRefTarget({ kind: 'reversal', id: v });
            } else if (record.businessRefType === 'MANUAL_GRANT') {
              // 手动赠送来源：businessRefId 为 trial_grant_records 记录 ID → 打开赠课记录明细（SC-35）
              setRefTarget({ kind: 'manualGrant', id: v });
            } else if (record.businessRefType === 'PERIOD_PASS') {
              // 期限卡来源（O-14）：businessRefId 为期限卡凭证 ID → 打开只读摘要抽屉（SC-11）
              setRefTarget({ kind: 'periodPass', id: v });
            } else {
              // 未知来源类型直接回退说明抽屉；已知类型单据不存在（404）时由各抽屉 onNotFound 切换回退（SC-16）
              setRefTarget({ kind: 'fallback', refType: record.businessRefType, refId: v });
            }
          }}
        >
          <EllipsisText value={v} />
        </a>
      ),
    },
    { title: '操作人', dataIndex: 'operatorName', render: (v) => v ?? '-' },
  ]);

  const detail = useEntryDetailWhen(entryDetailId);
  /** 下钻目标单据不存在（404，如历史汇总引用）→ 切换只读说明抽屉（SC-16）。 */
  const showRefFallback = useCallback(
    (refType: LedgerEntryListItem['businessRefType'], refId: string) => {
      setRefTarget({ kind: 'fallback', refType, refId });
    },
    [],
  );
  const reversalOriginal = useEntryDetailWhen(
    refTarget?.kind === 'reversal' ? refTarget.id : null,
    (id) => showRefFallback('REVERSAL', id),
  );

  return (
    <div>
      <div className="page-header-row">
        <div>
          <Title level={4} style={{ margin: 0 }}>
            学生课时账本
          </Title>
          <Text type="secondary">
            账户余额由课时流水汇总生成，不提供修改余额或删除流水的入口；选中持卡学生可在下方查看期限卡卡片
          </Text>
        </div>
      </div>

      <Card size="small" style={{ marginTop: 12 }}>
        <Space wrap>
          <Select
            showSearch
            allowClear
            placeholder="搜索学生姓名 / 学号"
            style={{ width: 220 }}
            filterOption={false}
            value={studentId}
            onSearch={handleStudentSearch}
            onChange={handleStudentChange}
            notFoundContent={studentKeyword ? '无匹配学生' : '输入姓名或学号联想'}
            options={studentSelectOptions}
          />
          <Select
            allowClear
            placeholder="选择账户"
            style={{ width: 220 }}
            disabled={!studentId}
            value={accountId}
            onChange={setAccountId}
            options={accounts.map((account) => ({
              value: account.accountId,
              label: account.skuName,
            }))}
          />
          <Select
            allowClear
            placeholder="流水类型"
            style={{ width: 160 }}
            value={entryType}
            onChange={setEntryType}
            options={typeOptions}
          />
        </Space>
        {summary && (
          <Alert
            type="info"
            style={{ marginTop: 12 }}
            message={`账户摘要：${summary.skuName} — 正式课可用 ${summary.formalAvailable}（冻结 ${summary.formalFrozen}），赠送课可用 ${summary.giftedAvailable}（冻结 ${summary.giftedFrozen}），状态「${ledgerStatusTag[summary.status].text}」`}
          />
        )}
      </Card>

      {studentId && periodPasses.length > 0 && (
        <PeriodPassSection
          passes={periodPasses}
          canAdjust={canAdjust}
          onAdjust={setAdjustingPass}
        />
      )}

      <Card size="small" style={{ marginTop: 12 }}>
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          components={components}
          dataSource={rows}
          locale={{ emptyText: '暂无流水' }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            onChange: (target) => void loadEntries(target),
          }}
        />
      </Card>

      <LedgerDetailDrawer detail={detail} onClose={() => setEntryDetailId(null)} />

      <DeductionRefDrawer
        deductionId={refTarget?.kind === 'deduction' ? refTarget.id : null}
        onClose={() => setRefTarget(null)}
        onNotFound={(id) => showRefFallback('DEDUCTION', id)}
      />
      <LedgerDetailDrawer
        detail={refTarget?.kind === 'reversal' ? reversalOriginal : null}
        onClose={() => setRefTarget(null)}
      />
      {refTarget?.kind === 'enrollment' && (
        <EnrollmentDetailDrawer
          applicationId={refTarget.id}
          onClose={() => setRefTarget(null)}
          onChanged={() => undefined}
          onNotFound={() => showRefFallback('ENROLLMENT', refTarget.id)}
        />
      )}
      {refTarget?.kind === 'order' && (
        <OrderDetailDrawer
          orderId={refTarget.id}
          onClose={() => setRefTarget(null)}
          onNotFound={() => showRefFallback('ORDER', refTarget.id)}
        />
      )}
      <ManualGrantRefDrawer
        recordId={refTarget?.kind === 'manualGrant' ? refTarget.id : null}
        onClose={() => setRefTarget(null)}
        onNotFound={(id) => showRefFallback('MANUAL_GRANT', id)}
      />
      <PeriodPassSummaryDrawer
        passId={refTarget?.kind === 'periodPass' ? refTarget.id : null}
        onClose={() => setRefTarget(null)}
        onNotFound={(id) => showRefFallback('PERIOD_PASS', id)}
      />
      <BusinessRefFallbackDrawer
        open={refTarget?.kind === 'fallback'}
        refType={refTarget?.kind === 'fallback' ? refTarget.refType : null}
        refId={refTarget?.kind === 'fallback' ? refTarget.refId : null}
        onClose={() => setRefTarget(null)}
      />
      {adjustingPass && studentId && (
        <PeriodAdjustModal
          pass={adjustingPass}
          studentId={studentId}
          onClose={() => setAdjustingPass(null)}
          onAdjusted={() => {
            // 成功后关闭对话框并即时刷新卡片列表与流水（新区间 + ADJUST 流水可见，SC-13）
            setAdjustingPass(null);
            void reloadPeriodPasses();
            void loadEntries(1);
          }}
        />
      )}
    </div>
  );
}
